<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

const props = defineProps({
  contrato: {
    type: Object,
    required: true,
  },
  explotacionDesc: {
    type: String,
    default: '',
  },
});


const puntoSuministro = computed(() => props.contrato.rawData?.GenericoContratoDTO?.puntoSuministro || {});
const datosPersonales = computed(() => props.contrato.rawData?.GenericoContratoDTO?.datosPersonales || {});
const datosPago = computed(() => props.contrato.rawData?.GenericoContratoDTO?.datosPago || {});
</script>

<template>
  <div class="space-y-6">
    <!-- Información del Cliente -->
    <div class="bg-n-background border border-n-weak rounded-lg p-4">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
        <span class="i-lucide-user" />
        Información del Cliente
      </h3>
      <div class="grid gap-4 md:grid-cols-2">
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Nombre del Cliente</p>
          <p class="text-sm font-medium text-n-slate-12">{{ contrato.titular }}</p>
        </div>
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Explotación</p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ explotacionDesc || contrato.explotacion }}
          </p>
        </div>
        <div class="md:col-span-2">
          <p class="text-xs text-n-slate-11 mb-1">Dirección de Suministro</p>
          <div class="flex items-start gap-2">
            <span class="i-lucide-map-pin text-n-slate-11 mt-0.5" />
            <p class="text-sm font-medium text-n-slate-12">{{ contrato.direccion }}</p>
          </div>
        </div>
        <div v-if="contrato.email">
          <p class="text-xs text-n-slate-11 mb-1">Email</p>
          <p class="text-sm font-medium text-n-slate-12 break-all">{{ contrato.email }}</p>
        </div>
        <div v-if="contrato.telefono">
          <p class="text-xs text-n-slate-11 mb-1">Teléfono</p>
          <p class="text-sm font-medium font-mono text-n-slate-12">{{ contrato.telefono }}</p>
        </div>
        <div v-if="datosPersonales.cifNif">
          <p class="text-xs text-n-slate-11 mb-1">NIF/RFC</p>
          <p class="text-sm font-medium font-mono text-n-slate-12">{{ datosPersonales.cifNif }}</p>
        </div>
      </div>
    </div>

    <!-- Información del Contrato -->
    <div class="bg-n-background border border-n-weak rounded-lg p-4">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
        <span class="i-lucide-file-text" />
        Información del Contrato
      </h3>
      <div class="grid gap-4 md:grid-cols-2">
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Número de Contrato</p>
          <p class="text-sm font-medium font-mono text-n-slate-12">{{ contrato.numeroContrato }}</p>
        </div>
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Estado</p>
          <Badge
            :color="contrato.estado === 'activo' ? 'green' : 'gray'"
            size="small"
          >
            {{ contrato.estado === 'activo' ? 'Activo' : 'Inactivo' }}
          </Badge>
        </div>
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Tipo de Uso</p>
          <Badge color="slate" size="small">{{ contrato.descUso }}</Badge>
        </div>
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Fecha de Alta</p>
          <p class="text-sm font-medium text-n-slate-12">{{ contrato.fechaAlta }}</p>
        </div>
        <div v-if="contrato.fechaBaja">
          <p class="text-xs text-n-slate-11 mb-1">Fecha de Baja</p>
          <p class="text-sm font-medium text-n-ruby-11">{{ contrato.fechaBaja }}</p>
        </div>
      </div>
    </div>

    <!-- Información del Contador -->
    <div class="bg-n-background border border-n-weak rounded-lg p-4">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
        <span class="i-lucide-droplet" />
        Información del Contador
      </h3>
      <div class="grid gap-4 md:grid-cols-2">
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Número de Serie</p>
          <p class="text-sm font-medium font-mono text-n-slate-12">
            {{ puntoSuministro.listaDeContadores?.ContratoContadorDTO?.numeroSerie || contrato.numeroContador }}
          </p>
        </div>
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Estado</p>
          <Badge
            :color="puntoSuministro.listaDeContadores?.ContratoContadorDTO?.estadoContador === '1' ? 'green' : 'gray'"
            size="small"
          >
            {{ puntoSuministro.listaDeContadores?.ContratoContadorDTO?.estadoContador === '1' ? 'Activo' : 'Inactivo' }}
          </Badge>
        </div>
        <div v-if="puntoSuministro.listaDeContadores?.ContratoContadorDTO?.fechaInstalacion">
          <p class="text-xs text-n-slate-11 mb-1">Fecha de Instalación</p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ new Date(puntoSuministro.listaDeContadores.ContratoContadorDTO.fechaInstalacion).toLocaleDateString('es-MX') }}
          </p>
        </div>
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Telelectura</p>
          <Badge :color="contrato.rawData?.GenericoContratoDTO?.contrato?.telelectura === 'true' ? 'green' : 'gray'" size="small">
            {{ contrato.rawData?.GenericoContratoDTO?.contrato?.telelectura === 'true' ? 'Sí' : 'No' }}
          </Badge>
        </div>
      </div>
    </div>

    <!-- Datos de Facturación y Pago -->
    <div class="bg-n-background border border-n-weak rounded-lg p-4">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
        <span class="i-lucide-dollar-sign" />
        Datos de Facturación y Pago
      </h3>
      <div class="space-y-4">
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Dirección de Facturación</p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ datosPago.direccionFacturacion || contrato.direccion }}
          </p>
        </div>
        <div>
          <p class="text-xs text-n-slate-11 mb-1">Forma de Pago</p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ datosPago.formaPago || 'No especificada' }}
          </p>
        </div>
        <div v-if="contrato.rawData?.GenericoContratoDTO?.contrato?.tienePlanPago === 'S'">
          <Badge color="blue" size="small" class="gap-1">
            <span class="i-lucide-credit-card" />
            Tiene Plan de Pago Activo
          </Badge>
        </div>
      </div>
    </div>

  </div>
</template>
